import envConfig from '../../static/config';
import config from '../config';
import ApiUtils from './ApiUtils';
import TransactionsActionCreator from '../actions/TransactionsActionCreator';
import TransactionsParser from '../parsers/TransactionsParser';

const _getTransactionsListEndPoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/transactions/searches/matchAll';
const _getTransactionFilterListEndpoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/transactions/searches/matchAllWithFilter';
const _getTagListEndpoint = '/ibapi/v2/banks/{bank-id}/tags/active';
const _assignTagEndpoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/{account-id}/transactions/metadata/tags';
const _getTransactionSearchSuggestionEndPoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/transactions/searches/suggest';
const _getSearchTransactionsEndpoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/transactions/searches/matchText';
const _getFilteredSearchTransactionsEndpoint =
  '/ibapi/v2/banks/{bank-id}/accounts/default/transactions/searches/matchTextWithFilter';
const _addNewTagEndPoint = '/ibapi/v2/banks/{bank-id}/tags';
const _editExistingTagEndPoint = '/ibapi/v2/banks/{bank-id}/tags/{tag-id}';
const _deleteExistingTagEndPoint =
  '/ibapi/v2/banks/{bank-id}/tags/{tag-id}/archive';
const _getEnrichedDataEndpoint =
  '/ibapi/v2/banks/{bank-id}/transactions/enrich';

class TransactionsApiUtils {
  /*
   ** API Utils service call to get Transactions
   */

  static getTransactions(
    accID,
    sortField,
    sortOrder,
    fromTransactionIndex,
    startDate,
    endDate
  ) {
    let url = envConfig.apiBaseUrlPAAS + _getTransactionsListEndPoint;
    let method = 'POST';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }
    const requestData = TransactionsParser.getTransactionsRequestPacket(
      accID,
      sortField,
      sortOrder,
      fromTransactionIndex,
      startDate,
      endDate
    );
    ApiUtils.makeAjaxCall(
      {
        // apiVersion: config.apiVersions.timelineService,
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTransactionSuccess(body);
      },
      (error, status) => {
        TransactionsActionCreator.handleTransactionServiceError(error, status);
      }
    );
  }

  static getEnrichedData(fetchedTransactions) {
    const { searchCriteria, transactionIdentifiers } =
      TransactionsParser.getEnrichedDataRequestPacket(fetchedTransactions);

    // Batch full of transactions that should not be enriched - do not send to third party
    if (searchCriteria.length === 0) {
      return;
    }

    let url = envConfig.apiBaseUrlPAAS + _getEnrichedDataEndpoint;
    let method = 'POST';
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData: {
          locale: 'en-GB', // Mandatory, hardcoded field
          searchCriteria,
        },
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleEnrichedDataSuccess(
          body,
          transactionIdentifiers
        );
      },
      (error) => {
        TransactionsActionCreator.handleEnrichedDataError(error);
      }
    );
  }

  /*
   ** API Utils service call to get Filtered Transactions
   */

  static getFilteredTransactions(
    accID,
    sortField,
    sortOrder,
    fromTransactionIndex,
    typeFilter,
    startDate,
    endDate
  ) {
    let url = envConfig.apiBaseUrlPAAS + _getTransactionFilterListEndpoint;
    let method = 'POST';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }
    const requestData = TransactionsParser.getFilteredTransactionsRequestPacket(
      accID,
      sortField,
      sortOrder,
      fromTransactionIndex,
      typeFilter,
      startDate,
      endDate
    );
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTransactionSuccess(body);
      },
      (error, status) => {
        TransactionsActionCreator.handleTransactionServiceError(error, status);
      }
    );
  }

  /*
   ** API Utils service call to get Transactions Tags
   */

  static getTags() {
    const url = envConfig.apiBaseUrlPAAS + _getTagListEndpoint;
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.tagManagementService,
        method: 'GET',
        url,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTagsListSuccess(body);
      },
      (error) => {
        TransactionsActionCreator.handleTagsServiceError(error);
      }
    );
  }

  /*
   ** API Utils service call to tag selected transactions
   */
  static tagSelectedTransactions(transactions, tag, accountId) {
    let url = envConfig.apiBaseUrlPAAS + _assignTagEndpoint;
    let method = 'PUT';
    url = url.replace('{account-id}', accountId);
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.financialStoresStub.TagConnection;
      method = 'GET';
    }
    const requestData = TransactionsParser.getAssignTagRequestPacket(
      transactions,
      tag
    );
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.tagManagementService,
        method,
        url,
        addAuthToken: true,
        requestData,
      },
      () => {
        TransactionsActionCreator.handleAssignTagSuccess();
      },
      (error) => {
        TransactionsActionCreator.handleAssignTagError(error);
      }
    );
  }

  static getTransactionsSearchSuggestion(searchText, accountId) {
    let url =
      envConfig.apiBaseUrlPAAS + _getTransactionSearchSuggestionEndPoint;
    let method = 'POST';
    const requestData =
      TransactionsParser.getTransactionSuggestionRequestPacket(
        searchText,
        accountId
      );
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineSearchSuggestion;
      method = 'GET';
    }
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTransactionSearchSuggestionSuccess(
          body
        );
      },
      (error) => {
        const e = error;
        e.isSearchSuggetionError = true;
        // TransactionsActionCreator.handleError();
      }
    );
  }

  static getSearchTransactions(
    accountId,
    sortField,
    sortOrder,
    fromTransactionIndex,
    suggestData,
    startDate,
    endDate
  ) {
    let url = envConfig.apiBaseUrlPAAS + _getSearchTransactionsEndpoint;
    let method = 'POST';
    const requestData = TransactionsParser.getSearchTransactionsRequestPacket(
      accountId,
      sortField,
      sortOrder,
      fromTransactionIndex,
      suggestData,
      startDate,
      endDate
    );

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTransactionSuccess(body);
      },
      (error, status) => {
        TransactionsActionCreator.handleTransactionServiceError(error, status);
      }
    );
  }

  static getFilteredSearchTransactions(
    accountId,
    sortField,
    sortOrder,
    typeFilter,
    fromTransactionIndex,
    suggestData,
    startDate,
    endDate
  ) {
    let url = envConfig.apiBaseUrlPAAS + _getFilteredSearchTransactionsEndpoint;
    let method = 'POST';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }
    const requestData =
      TransactionsParser.getFilteredSearchTransactionsRequestPacket(
        accountId,
        sortField,
        sortOrder,
        typeFilter,
        fromTransactionIndex,
        suggestData,
        startDate,
        endDate
      );
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      (body) => {
        TransactionsActionCreator.handleTransactionSuccess(body);
      },
      (error, status) => {
        TransactionsActionCreator.handleTransactionServiceError(error, status);
      }
    );
  }

  static addNewTag(newTagValue) {
    let url = envConfig.apiBaseUrlPAAS + _addNewTagEndPoint;
    let method = 'POST';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }

    const requestData = TransactionsParser.getAddNewTagPacket(newTagValue);
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      () => {
        TransactionsActionCreator.getTags();
      },
      (error) => {
        TransactionsActionCreator.handleAssignTagError(error);
      }
    );
  }

  static editExistingTag(keyid, editTagValue) {
    let url = envConfig.apiBaseUrlPAAS + _editExistingTagEndPoint;
    url = url.replace('{tag-id}', keyid);
    let method = 'PUT';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }
    const requestData = TransactionsParser.getEditExistingTagPacket(
      keyid,
      editTagValue
    );
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        requestData,
        addAuthToken: true,
      },
      () => {
        TransactionsActionCreator.getTags();
      },
      (error) => {
        TransactionsActionCreator.handleAssignTagError(error);
      }
    );
  }

  static deleteExistingTag(deleteTagId) {
    let url = envConfig.apiBaseUrlPAAS + _deleteExistingTagEndPoint;
    url = url.replace('{tag-id}', deleteTagId);
    let method = 'PUT';
    if (envConfig.stubConfig) {
      url = envConfig.stubConfig.timelineStub.TimelineListConnection;
      method = 'GET';
    }
    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.timelineService,
        method,
        url,
        addAuthToken: true,
        contentType: 'N',
      },
      () => {
        TransactionsActionCreator.getTags();
      },
      (error) => {
        TransactionsActionCreator.handleAssignTagError(error);
      }
    );
  }
}

export default TransactionsApiUtils;
